import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/http'
import { useRpcEvent } from '../api/rpc'
import type { UsageRow, UsageStatus, UsageSummary } from '../api/types'
import { Card } from '../components/ui/Card'
import { Table, Td, Th, Tile, Tiles } from '../components/ui/DataTable'
import { Select } from '../components/ui/Select'
import { formatDateTime, formatUsd } from '../lib/format'

/**
 * The signed-in user's own usage: budget and spend for the current or previous period, plus the
 * per-model and per-source breakdown. One table per list; the period picker lives in the panel head.
 */

/** Panel head (mockup `.panel-head`): eyebrow, title, lede, status line, action. */
function PanelHead({ eyebrow, title, lede, status, action }: {
  eyebrow: ReactNode; title: ReactNode; lede?: ReactNode; status?: ReactNode; action?: ReactNode
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-6 border-b border-line pb-4">
      <div className="min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-500">{eyebrow}</p>
        <h1 className="mt-2 font-display text-[30px] font-bold tracking-tight">{title}</h1>
        {lede && <p className="mt-2 max-w-[70ch] text-sm text-ink-400">{lede}</p>}
        {status && <p className="mt-2 text-[13.5px] text-ink-400">{status}</p>}
      </div>
      {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
    </header>
  )
}

/** One breakdown table (by model or by source) with a total in the footer. */
function BreakdownTable({ rows, label }: { rows: UsageRow[]; label: string }) {
  const { t } = useTranslation()
  if (rows.length === 0) return <p className="text-sm text-ink-500">{t('usage.none')}</p>
  const total = rows.reduce((n, r) => n + r.cost, 0)
  return (
    <Table
      minWidth={560}
      footer={<>
        <span>{t('usage.totalPeriod')}</span>
        <span className="flex-1" />
        <span className="font-mono tabular-nums">{formatUsd(total)}</span>
      </>}
    >
      <thead>
        <tr>
          <Th>{label}</Th>
          <Th>{t('usage.scope')}</Th>
          <Th className="text-right">{t('usage.tokensIn')}</Th>
          <Th className="text-right">{t('usage.tokensOut')}</Th>
          <Th className="text-right">{t('usage.cost')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={`${r.key}-${r.scope}`} className="border-b border-cream-200 last:border-b-0">
            <Td className="max-w-64 truncate font-mono text-xs">{r.key}</Td>
            <Td><span className="text-xs text-ink-400">{t(`usage.scope_${r.scope}`)}</span></Td>
            <Td className="text-right tabular-nums">{r.input.toLocaleString()}</Td>
            <Td className="text-right tabular-nums">{r.output.toLocaleString()}</Td>
            <Td className="text-right tabular-nums">{formatUsd(r.cost)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

export function UsagePage() {
  const { t, i18n } = useTranslation()
  const [period, setPeriod] = useState<'current' | 'previous'>('current')
  const [data, setData] = useState<{ status: UsageStatus; summary: UsageSummary } | null>(null)
  useEffect(() => {
    void api.get<{ status: UsageStatus; summary: UsageSummary }>(`/api/me/usage?period=${period}`).then(setData)
  }, [period])
  useRpcEvent('usage.updated', (s: UsageStatus) => setData(d => (d ? { ...d, status: s } : d)))

  const s = data?.status
  const summary = data?.summary
  const pct = s && s.budgetUsd ? Math.min(100, (s.sharedCostUsd / s.budgetUsd) * 100) : 0
  const remaining = s && s.budgetUsd !== null ? Math.max(0, s.budgetUsd - s.sharedCostUsd) : null
  const elapsed = s ? Math.min(1, Math.max(0, (Date.now() - s.periodStart) / Math.max(1, s.periodEnd - s.periodStart))) : 0
  const forecast = s ? (elapsed > 0 ? s.sharedCostUsd / elapsed : s.sharedCostUsd) : 0

  return (
    <>
      <PanelHead
        eyebrow={t('usage.title')}
        title={t('usage.title')}
        lede={s
          ? t('usage.periodStart', { date: formatDateTime(s.periodStart, i18n.language), date2: formatDateTime(s.periodEnd, i18n.language) })
          : t('usage.subtitle')}
        status={s && (
          <>
            <b>{formatUsd(s.sharedCostUsd)}</b>
            <span className="text-ink-500"> · </span>
            <b>{s.budgetUsd === null ? t('usage.unlimited') : formatUsd(s.budgetUsd)}</b>
            <span className="text-ink-500"> · </span>
            {t('usage.resets', { date: formatDateTime(s.periodEnd, i18n.language) })}
          </>
        )}
        action={
          <div className="w-44">
            <Select
              value={period} onChange={v => setPeriod(v)} label={t('usage.period')}
              options={[
                { value: 'current', label: t('usage.current') },
                { value: 'previous', label: t('usage.previous') },
              ]}
            />
          </div>
        }
      />

      {s && (
        <>
          <Tiles>
            <Tile
              label={t('usage.spentThisPeriod')} value={formatUsd(s.sharedCostUsd)} rail="attention"
              note={t('usage.usedPct', { pct: Math.round(pct) })}
            />
            <Tile label={t('usage.budget')} value={s.budgetUsd === null ? t('usage.unlimited') : formatUsd(s.budgetUsd)} />
            <Tile label={t('usage.remaining')} value={remaining === null ? '—' : formatUsd(remaining)} />
            <Tile label={t('usage.forecast')} value={formatUsd(forecast)} note={t('usage.forecastNote')} />
          </Tiles>

          <div className="mt-5">
            <Card title={t('usage.budget')}>
              <p className="font-display text-3xl font-semibold">
                {formatUsd(s.sharedCostUsd)} <span className="text-base text-ink-500">/ {s.budgetUsd === null ? t('usage.unlimited') : formatUsd(s.budgetUsd)}</span>
              </p>
              {s.budgetUsd !== null && (
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-cream-200">
                  <div
                    className={pct >= 100 ? 'h-full bg-red-400' : pct >= 80 ? 'h-full bg-amber-400' : 'h-full bg-mint-500'}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
              <p className="mt-2 text-xs text-ink-500">{t('usage.resets', { date: formatDateTime(s.periodEnd, i18n.language) })}</p>
              <p className="mt-1 text-xs text-ink-500">{t('usage.personalNote')}</p>
            </Card>
          </div>
        </>
      )}

      {summary && (
        <div className="mt-5 space-y-5">
          <Card title={t('usage.byModelTitle')}>
            <BreakdownTable rows={summary.byModel} label={t('usage.byModel')} />
          </Card>
          <Card title={t('usage.bySourceTitle')}>
            <BreakdownTable rows={summary.bySource} label={t('usage.bySource')} />
          </Card>
        </div>
      )}
    </>
  )
}
